import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";
import customIcon from "../Assets/customIcon.png";
import {
  signInWithGoogleAccount,
  signInWithFacebookAccount,
  signInWithYouTubeAccount,
  getSocialErrorMessage,
} from "../services/SocialAuthService";

const API_BASE = API_BASE_URL;
const DEFAULT_FCM = "web-client"; // fallback when no push token on web
const DEFAULT_HASH = "web-hash"; // backend requires non-empty hash

const Login = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [socialLoading, setSocialLoading] = useState("");
  const [socialMsg, setSocialMsg] = useState("");

  useEffect(() => {
    // Redirect if already logged in
    const existing = localStorage.getItem("userMobileNumber");
    if (existing) {
      navigate("/");
    }
  }, [navigate]);

  const isValidMobile = (value) => /^\d{10}$/.test(value);

  const handleSocial = async (provider) => {
    setError("");
    setSocialMsg("");
    setSocialLoading(provider);
    try {
      let profile;
      if (provider === "google") profile = await signInWithGoogleAccount();
      else if (provider === "facebook") profile = await signInWithFacebookAccount();
      else if (provider === "youtube") profile = await signInWithYouTubeAccount();
      else {
        setSocialMsg("Instagram login abhi setup ho raha hai — Mobile OTP se login karein.");
        setSocialLoading("");
        return;
      }
      setSocialMsg(
        `✅ ${profile.name || profile.email || "Login successful"}! Ab apna mobile number verify karein — ek baar ka kaam hai.`
      );
    } catch (err) {
      const msg = getSocialErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setSocialLoading("");
    }
  };


  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidMobile(mobile)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setLoading(true);
      const fcmToken = localStorage.getItem("fcm_token") || DEFAULT_FCM;
      const hashCode = localStorage.getItem("sms_hash") || DEFAULT_HASH;
      const res = await fetch(`${API_BASE}/otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: mobile,
          FCM: fcmToken,
          hash: hashCode,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }

      const message = String(data?.message || data?.Message || "").toLowerCase();
      const isSuccess =
        res.ok &&
        (message.includes("otp sent") ||
          message.includes("success") ||
          message.includes("sent") ||
          data?.otp ||
          data?.success === true ||
          data?.status === "success");

      if (isSuccess) {
        setSuccess("OTP sent successfully");
        navigate("/otp", {
          state: {
            mobileNumber: mobile,
            serverOtp: data?.otp,
            firstTime: data?.firstTime,
            fcmToken,
            hashCode,
          },
        });
      } else {
        throw new Error(data?.message || data?.Message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.message || "Unable to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <Seo
        title="Login | Free Account"
        description="AIO India pe free mein login karo aur apne products bechein ya khareedein. Mobile se OTP ke saath instant login."
        path="/login"
      />
      <style>{keyframes}</style>
      <div style={styles.blob1} />
      <div style={styles.blob2} />
      <div style={styles.card}>
        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <img
            src={customIcon}
            alt="AIO INDIA"
            style={{
              width: "64px",
              height: "64px",
              objectFit: "contain",
              borderRadius: "18px",
              boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
              background: "rgba(255,255,255,0.12)",
              padding: "8px",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          />
          <h1 style={{ ...styles.title, marginTop: "14px" }}>Welcome to AIO India</h1>
        </div>
        <div style={styles.badge}>✨ Secure Login</div>
        <p style={styles.subtitle}>Enter your mobile number to receive an OTP.</p>

        <form onSubmit={handleSendOtp} style={styles.form}>
          <label style={styles.label}>Mobile number</label>
          <div style={styles.inputRow}>
            <span style={styles.prefix}>+91</span>
            <input
              style={styles.input}
              value={mobile}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                setMobile(v);
              }}
              placeholder="10-digit number"
              inputMode="numeric"
              maxLength={10}
              required
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? "Sending..." : "Send OTP"}
          </button>
        </form>

        <div style={styles.helper}>We’ll send a one-time password to your phone.</div>

        {/* ===== SOCIAL LOGIN ===== */}
        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or continue with</span>
          <span style={styles.dividerLine} />
        </div>

        <div style={styles.socialGrid}>
          <button
            type="button"
            onClick={() => handleSocial("google")}
            disabled={Boolean(socialLoading)}
            style={styles.socialBtn}
          >
            <span style={{ ...styles.socialIcon, background: "#fff", color: "#4285F4" }}>G</span>
            {socialLoading === "google" ? "..." : "Google"}
          </button>

          <button
            type="button"
            onClick={() => handleSocial("facebook")}
            disabled={Boolean(socialLoading)}
            style={styles.socialBtn}
          >
            <span style={{ ...styles.socialIcon, background: "#1877F2", color: "#fff" }}>f</span>
            {socialLoading === "facebook" ? "..." : "Facebook"}
          </button>

          <button
            type="button"
            onClick={() => handleSocial("instagram")}
            disabled={Boolean(socialLoading)}
            style={styles.socialBtn}
          >
            <span style={{ ...styles.socialIcon, background: "linear-gradient(45deg,#f09433,#dc2743,#bc1888)", color: "#fff" }}>📷</span>
            Instagram
          </button>

          <button
            type="button"
            onClick={() => handleSocial("youtube")}
            disabled={Boolean(socialLoading)}
            style={styles.socialBtn}
          >
            <span style={{ ...styles.socialIcon, background: "#FF0000", color: "#fff" }}>▶</span>
            {socialLoading === "youtube" ? "..." : "YouTube"}
          </button>
        </div>

        {socialMsg && <div style={styles.socialMsg}>{socialMsg}</div>}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "90vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #312e81 70%, #0f172a 100%)",
  },
  blob1: {
    position: "absolute",
    top: "-120px",
    right: "-100px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)",
    filter: "blur(40px)",
    pointerEvents: "none",
  },
  blob2: {
    position: "absolute",
    bottom: "-100px",
    left: "-80px",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)",
    filter: "blur(40px)",
    pointerEvents: "none",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#0f172a",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
    color: "#e2e8f0",
    position: "relative",
    overflow: "hidden",
    animation: "fadeInUp 600ms ease",
  },
  badge: {
    display: "inline-block",
    padding: "6px 12px",
    background: "rgba(59,130,246,0.15)",
    color: "#93c5fd",
    borderRadius: "999px",
    fontSize: "12px",
    letterSpacing: "0.5px",
    marginBottom: "14px",
  },
  title: { margin: 0, fontSize: "26px", fontWeight: 700 },
  subtitle: { margin: "6px 0 18px", color: "#94a3b8", fontSize: "15px" },
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  label: { fontSize: "14px", color: "#cbd5e1" },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    transition: "border 200ms ease, background 200ms ease",
  },
  prefix: {
    color: "#a5b4fc",
    fontWeight: 600,
    fontSize: "14px",
  },
  input: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: "#e2e8f0",
    fontSize: "16px",
    letterSpacing: "1px",
  },
  button: {
    marginTop: "6px",
    width: "100%",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "white",
    fontWeight: 700,
    letterSpacing: "0.3px",
    boxShadow: "0 10px 30px rgba(37,99,235,0.35)",
    transition: "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
  },
  error: {
    background: "rgba(248,113,113,0.12)",
    color: "#fecdd3",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "13px",
    border: "1px solid rgba(248,113,113,0.35)",
  },
  success: {
    background: "rgba(74,222,128,0.12)",
    color: "#bbf7d0",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "13px",
    border: "1px solid rgba(74,222,128,0.35)",
  },
  helper: { marginTop: "12px", color: "#94a3b8", fontSize: "13px" },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    margin: "20px 0 14px",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "rgba(255,255,255,0.12)",
  },
  dividerText: { color: "#64748b", fontSize: "12px", whiteSpace: "nowrap" },
  socialGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  socialBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "11px 10px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.05)",
    color: "#e2e8f0",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 200ms ease, transform 150ms ease",
  },
  socialIcon: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 800,
    flexShrink: 0,
  },
  socialMsg: {
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.3)",
    color: "#86efac",
    fontSize: "13px",
    lineHeight: 1.5,
  },
};

const keyframes = `
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
button:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(37,99,235,0.45); }
button:disabled { opacity: 0.7; cursor: not-allowed; }
`;

export default Login;

